import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CardInfoQty from '@/domains/dashboard-management/components/CardInfoQty/CardInfoQty';
import CardOrder from '@/domains/dashboard-management/components/CardOrder/CardOrder';
import CardStock from '@/domains/dashboard-management/components/CardStock/CardStock';
import CardTotal from '@/domains/dashboard-management/components/CardTotal/CardTotal';
import ListStock from '@/domains/dashboard-management/components/ListStock/ListStock';
import {
  useDashboardSummary,
  useLatestTransaction,
  useStockHighlights,
} from '@/domains/dashboard-management/hooks/useDashboard';
import { orderBadge } from '@/domains/dashboard-management/utils/orderStatus';
import { STATUS_COURIER } from '@/domains/mobile-web/constant/listStatusCourier.constant';
import { cn } from '@/lib/utils';
import Button from '@/shared/components/ui/Button';
import { formatDateToDDMonYYYY } from '@/shared/utils/date';
import { useColorStore } from '@/store/colorStore';

const HomeManagement = () => {
  const { NEWCOLOR } = useColorStore();
  const navigate = useNavigate();

  const { data: summary } = useDashboardSummary();
  const { data: latest, isLoading: latestLoading } = useLatestTransaction();
  const { data: stock, isLoading: stockLoading } = useStockHighlights();

  const badge = latest ? STATUS_COURIER[orderBadge(latest)] : null;
  const reported = latest?.transfer_status === 'reported';
  const outOfStock = (stock?.out_of_stock ?? []).map((item) => ({ name: item.title }));
  const lowStock = (stock?.low_stock ?? []).map((item) => ({
    name: item.title,
    qty: item.available,
  }));

  return (
    <section className="flex! flex-col gap-6 overflow-auto mb-10">
      <h1 className="capitalize title2medium">beranda</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 md:gap-6 gap-3">
        <CardTotal title="total produk" total={summary?.total_products ?? '-'} unit="buku" />
        <CardTotal title="Total Stok" total={summary?.total_stock ?? '-'} unit="Unit" />
        <CardTotal
          title="Total Transaksi"
          total={summary?.total_transactions ?? '-'}
          unit="Transaksi"
        />
        <CardTotal
          title="Total Anggota Affiliate"
          total={summary?.total_affiliates ?? '-'}
          unit="Anggota"
        />
      </div>

      <section className="flex! flex-col gap-3 w-full">
        <div className="inline-flex justify-between gap-3 items-center">
          <p className="capitalize title2medium">Transaksi Terakhir</p>
          <button
            type="button"
            className="inline-flex gap-3 text-text-blue-200"
            onClick={() => navigate('/dashboard/history')}
          >
            <span>Lihat Semua</span>
            <ChevronRight />
          </button>
        </div>

        <div className="shadowCard gap-3 flex flex-col p-6 w-full">
          {latest ? (
            <>
              <div className="inline-flex w-full justify-between gap-3 items-center">
                <p className="title1bold text-text-blue-200">{latest.order_number}</p>
                {badge && (
                  <p
                    className={cn(
                      'px-3 text-center py-1 rounded-[4px] w-fit capitalize title2medium'
                    )}
                    style={{ backgroundColor: NEWCOLOR[badge.bg], color: badge.color }}
                  >
                    {reported ? badge.label : (latest.status_label ?? badge.label)}
                  </p>
                )}
              </div>
              <div className="grid-cols-1 grid md:grid-cols-5 sm:grid-cols-2 gap-3">
                <CardInfoQty title="ID INVOICE" value={latest.order_number} />
                <CardInfoQty title="NAMA PEMBELI" value={latest.buyer_name} />
                <CardInfoQty title="TANGGAL" value={formatDateToDDMonYYYY(latest.placed_at)} />
                <CardInfoQty title="JUMLAH BUKU" value={latest.book_total} />
                <CardInfoQty title="JASA PENGIRIMAN" value={latest.courier_name ?? '-'} />
              </div>

              <article className="shadowCard  p-3 w-full flex flex-col gap-3 bg-[#F6F6F6]">
                <p className="capitalize title2medium text-text-blue-200">NAMA BUKU & QTY</p>
                <div className="flex flex-col  gap-3 w-full overflow-auto min-w-[100px] max-h-[300px]">
                  {latest.items.length > 0 ? (
                    <CardOrder
                      listBook={latest.items.map((item) => ({
                        name: item.title,
                        qty: item.quantity,
                      }))}
                    />
                  ) : (
                    <p className="text-center capitalize">tidak ada data</p>
                  )}
                </div>
                <div className=" divider h-[0.5px] w-full bg-blue-cs-200" />
                <div className="w-full flex flex-wrap justify-between items-center gap-3">
                  <div className="inline-flex gap-3 flex-wrap">
                    <span>nomor Resi:</span>
                    <span className="title2medium text-black">{latest.tracking_number ?? '-'}</span>
                  </div>
                  <Button
                    className="btn-fill-white-db flex-1 sm:flex-none md:flex-none"
                    onClick={() =>
                      navigate(`/dashboard/history-detail?id=${latest.order_id}`, {
                        state: { code: latest.order_number },
                      })
                    }
                  >
                    <span className="body1">Lihat Detail</span>
                  </Button>
                </div>
              </article>
            </>
          ) : (
            <p className="text-center capitalize">
              {latestLoading ? 'memuat...' : 'belum ada transaksi'}
            </p>
          )}
        </div>
      </section>

      <section className="flex! flex-col gap-3 w-full">
        <div className="inline-flex justify-between gap-3 items-center">
          <p className="capitalize title2medium">Highlight Stok</p>
          <button
            type="button"
            className="inline-flex gap-3 text-text-blue-200"
            onClick={() => navigate('/dashboard/product')}
          >
            <span>Lihat Semua</span>
            <ChevronRight />
          </button>
        </div>
        <div className="w-full gap-6 grid grid-cols-1 md:grid-cols-3">
          <div className="shadowCard p-6 gap-3 flex flex-col">
            <p className="capitalize title1bold text-text-blue-200">Stok Terlaris</p>
            {stock?.best_seller ? (
              <>
                <CardStock
                  title="NAMA BARANG"
                  name={stock.best_seller.title}
                  img={stock.best_seller.cover_url ?? undefined}
                />
                <CardStock
                  title="JUMLAH TRANSAKSI"
                  total={stock.best_seller.completed_orders}
                  name="Pesanan Selesai"
                />
              </>
            ) : (
              <p className="text-center capitalize">
                {stockLoading ? 'memuat...' : 'tidak ada data'}
              </p>
            )}
          </div>

          <div className="shadowCard p-6 gap-3 flex flex-col">
            <p className="capitalize title1bold text-text-blue-200">Stok Kosong</p>
            {outOfStock.length > 0 ? (
              <ListStock title="NAMA BARANG" listDataStock={outOfStock} />
            ) : (
              <p className="text-center capitalize">
                {stockLoading ? 'memuat...' : 'tidak ada data'}
              </p>
            )}
          </div>
          <div className="shadowCard p-6 gap-3 flex flex-col">
            <p className="capitalize title1bold text-text-blue-200">Stok Sedikit</p>
            {lowStock.length > 0 ? (
              <ListStock title="NAMA BARANG & STOK" listDataStock={lowStock} />
            ) : (
              <p className="text-center capitalize">
                {stockLoading ? 'memuat...' : 'tidak ada data'}
              </p>
            )}
          </div>
        </div>
      </section>
    </section>
  );
};

export default HomeManagement;
